import { useEffect, useRef, useState } from "react";
import { Box, Divider, Popover, TextField, Tooltip, Typography } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { isInvoiceHexColor } from "../utils/normalizeInvoiceCustomStyle";
import { invoiceColorSwatches, invoiceNeutralSwatches } from "../constants/invoiceColorSwatches";

interface InvoiceColorFieldProps {
  label: string;
  helper?: string;
  value: string;
  warning?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const expandHex = (value: string) => {
  const raw = value.trim().replace("#", "");

  return raw.length === 3 ? `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}` : `#${raw}`;
};

const normalizeHex = (value: string) => expandHex(value).toUpperCase();

const Swatch = ({ color, isActive, onSelect }: { color: string; isActive: boolean; onSelect: (color: string) => void }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-label={color}
    onClick={() => onSelect(color)}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect(color);
      }
    }}
    sx={{
      width: 20,
      height: 20,
      borderRadius: "6px",
      bgcolor: color,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: isActive ? "0 0 0 2px #0F172A, 0 0 0 4px #FFFFFF" : "inset 0 0 0 1px rgba(15,23,42,0.16)",
      transition: "transform 100ms",
      "&:hover": { transform: "scale(1.15)" },
    }}
  >
    {isActive ? <CheckRounded sx={{ fontSize: 13, color: "#FFFFFF", filter: "drop-shadow(0 0 1px rgba(0,0,0,0.6))" }} /> : null}
  </Box>
);

export const InvoiceColorField = ({ label, helper, value, warning, disabled, onChange }: InvoiceColorFieldProps) => {
  const [draft, setDraft] = useState(value);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const swatchRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = (next: string) => {
    if (!isInvoiceHexColor(next)) {
      setDraft(value);
      return;
    }

    onChange(normalizeHex(next));
  };

  const selectSwatch = (color: string) => {
    onChange(normalizeHex(color));
    setAnchorEl(null);
  };

  const hasError = draft.length > 0 && !isInvoiceHexColor(draft);
  const activeColor = isInvoiceHexColor(value) ? normalizeHex(value) : "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: disabled ? 0.45 : 1 }}>
      <Tooltip title={disabled ? "" : "Pick a color"}>
        <Box
          ref={swatchRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setAnchorEl(swatchRef.current)}
          onKeyDown={(event) => {
            if (!disabled && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              setAnchorEl(swatchRef.current);
            }
          }}
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: isInvoiceHexColor(value) ? value : "#FFFFFF",
            cursor: disabled ? "default" : "pointer",
            boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.14), 0 1px 2px rgba(15,23,42,0.10)",
            transition: "transform 120ms",
            "&:hover": disabled ? undefined : { transform: "scale(1.08)" },
          }}
        />
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 50000 }}
        slotProps={{ paper: { sx: { p: 1.5, borderRadius: 2, boxShadow: "0 12px 32px rgba(15,23,42,0.18)" } } }}
      >
        <Typography sx={{ fontSize: "0.6875rem", fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "text.secondary" }}>
          {label}
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 20px)", gap: 0.75, mt: 1 }}>
          {invoiceNeutralSwatches.map((color) => (
            <Swatch key={color} color={color} isActive={color === activeColor} onSelect={selectSwatch} />
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(10, 20px)", gap: 0.75 }}>
          {invoiceColorSwatches.flat().map((color, index) => (
            <Swatch key={`${color}-${index}`} color={color} isActive={color === activeColor} onSelect={selectSwatch} />
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              flexShrink: 0,
              borderRadius: "6px",
              bgcolor: isInvoiceHexColor(draft) ? draft : "#FFFFFF",
              boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.16)",
            }}
          />

          <TextField
            autoFocus
            size="small"
            placeholder="#RRGGBB"
            value={draft}
            error={hasError}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={(event) => commit(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commit((event.target as HTMLInputElement).value);
                setAnchorEl(null);
              }
            }}
            inputProps={{ maxLength: 7, spellCheck: false }}
            sx={{
              width: 120,
              "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: "0.8125rem", textTransform: "uppercase", px: 1, py: 0.75 },
            }}
          />
        </Box>
      </Popover>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1.25 }} noWrap>
            {label}
          </Typography>

          {warning ? (
            <Tooltip title={warning}>
              <WarningAmberRounded sx={{ fontSize: 15, color: "#D97706", flexShrink: 0 }} />
            </Tooltip>
          ) : null}
        </Box>

        {helper ? (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.3 }} noWrap>
            {helper}
          </Typography>
        ) : null}
      </Box>

      <TextField
        size="small"
        value={draft}
        error={hasError}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit((event.target as HTMLInputElement).value);
        }}
        inputProps={{ maxLength: 7, spellCheck: false }}
        sx={{
          width: 86,
          flexShrink: 0,
          "& .MuiOutlinedInput-input": {
            fontFamily: "monospace",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            px: 1,
            py: 0.75,
          },
        }}
      />
    </Box>
  );
};
